import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, RotateCcw, Check, Plus, Trash2, Layers, Banknote, Activity } from "lucide-react";
import api, { formatCurrency } from "../../lib/api";
import StatusBadge from "./StatusBadge";

/**
 * Right-side drawer for managing a single memo end-to-end:
 *  • Items tab — per-line partial return + partial sell (auto-converts to sale when settled)
 *  • Payments tab — record partial payments while memo is open; admin can void
 *  • Timeline tab — newest-first event log
 *
 * Inventory is the master source of truth — every action returns the freshly
 * decorated memo, so the drawer never holds stale state.
 */
export default function ManageMemoDrawer({ memo, isAdmin, onClose, onChange }) {
  const [tab, setTab] = useState("items"); // items | payments | timeline
  const [timeline, setTimeline] = useState([]);
  const [paymentForm, setPaymentForm] = useState({ amount: "", method: "cash", notes: "" });
  const [busy, setBusy] = useState(false);

  // Per-line action drafts: line_id → { return: "", sell: "" }
  const [drafts, setDrafts] = useState(() =>
    Object.fromEntries(memo.items.map((it) => [it.line_id, { return: "", sell: "" }])),
  );

  // Whenever the upstream memo refreshes, seed drafts for any new lines.
  useEffect(() => {
    setDrafts((prev) => {
      const next = { ...prev };
      memo.items.forEach((it) => { if (!next[it.line_id]) next[it.line_id] = { return: "", sell: "" }; });
      return next;
    });
  }, [memo.items]);

  // Lazy-load timeline when the tab is shown
  useEffect(() => {
    if (tab !== "timeline") return;
    (async () => {
      try {
        const { data } = await api.get(`/memos/${memo.id}/timeline`);
        setTimeline(data.events || []);
      } catch (e) { toast.error(e.response?.data?.detail || "Timeline failed"); }
    })();
  }, [tab, memo.id]);

  const setDraft = (lineId, field, value) =>
    setDrafts((p) => ({ ...p, [lineId]: { ...p[lineId], [field]: value } }));

  const isLocked = ["Cancelled", "Completed", "Converted to Sale"].includes(memo.status_effective);

  const collectDrafts = (field) => memo.items
    .map((it) => ({ line_id: it.line_id, qty: parseFloat(drafts[it.line_id]?.[field] || 0) }))
    .filter((l) => l.qty > 0);

  const clearDraftField = (field) =>
    setDrafts((p) => Object.fromEntries(Object.entries(p).map(([k, v]) => [k, { ...v, [field]: "" }])));

  const doReturn = async () => {
    const lines = collectDrafts("return");
    if (!lines.length) return toast.error("Enter at least one return quantity");
    setBusy(true);
    try {
      await api.post(`/memos/${memo.id}/lines/return`, { lines });
      toast.success("Return recorded");
      clearDraftField("return");
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Return failed"); }
    finally { setBusy(false); }
  };

  const doSell = async () => {
    const lines = collectDrafts("sell");
    if (!lines.length) return toast.error("Enter at least one sell quantity");
    setBusy(true);
    try {
      const { data } = await api.post(`/memos/${memo.id}/lines/sell`, { lines });
      toast.success(
        data.converted_sale_id
          ? `Auto-converted to invoice ${data.converted_invoice_number}`
          : "Sale recorded",
      );
      clearDraftField("sell");
      await onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Sell failed"); }
    finally { setBusy(false); }
  };

  const addPayment = async (e) => {
    e.preventDefault();
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than 0");
    setBusy(true);
    try {
      await api.post(`/memos/${memo.id}/payments`, { ...paymentForm, amount: amt });
      toast.success("Payment recorded");
      setPaymentForm({ amount: "", method: "cash", notes: "" });
      await onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const removePayment = async (p) => {
    if (!window.confirm(`Remove payment Rp ${p.amount.toLocaleString()}?`)) return;
    try {
      await api.delete(`/memos/${memo.id}/payments/${p.id}`);
      toast.success("Payment removed");
      await onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const totals = memo.totals || {};

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="manage-memo-drawer">
      <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-3xl bg-[#0F0F0F] border-l border-white/10 overflow-y-auto">
        {/* Header */}
        <div className="px-7 py-5 border-b border-white/5 flex justify-between items-start sticky top-0 bg-[#0F0F0F] z-10">
          <div>
            <div className="overline">{memo.memo_number}</div>
            <h3 className="serif text-2xl mt-1">{memo.customer_name}</h3>
            <div className="text-[0.7rem] mono text-[#8A8A8A] mt-1.5 flex items-center gap-3">
              <StatusBadge status={memo.status_effective} />
              {memo.due_date && <span>due {memo.due_date}</span>}
            </div>
            {memo.converted_invoice_number && (
              <div className="mt-2 text-[0.7rem] text-[#D4AF37] mono" data-testid="manage-converted-invoice">
                Auto-converted → {memo.converted_invoice_number}
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="manage-close" className="text-[#8A8A8A] hover:text-[#EAEAEA]">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-7 py-5 border-b border-white/5">
          <Totals label="Memo Value" value={formatCurrency(totals.total_memo_value)} />
          <Totals label="Returned" value={formatCurrency(totals.total_returned_value)} tone="muted" />
          <Totals label="Sold" value={formatCurrency(totals.total_sold_value)} tone="gold" testid="totals-sold" />
          <Totals label="Balance" value={formatCurrency(totals.remaining_balance)} tone="warn" testid="totals-balance" />
        </div>

        {/* Tabs */}
        <div className="border-b border-white/5 px-7 flex">
          {[
            { k: "items", label: "Items", icon: Layers },
            { k: "payments", label: `Payments (${(memo.payments || []).length})`, icon: Banknote },
            { k: "timeline", label: "Timeline", icon: Activity },
          ].map(({ k, label, icon: Icon }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`manage-tab-${k}`}
              className={`px-4 py-3 text-[0.7rem] uppercase tracking-wider inline-flex items-center gap-1.5 border-b-2 ${
                tab === k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-[#8A8A8A] hover:text-[#EAEAEA]"
              }`}
            >
              <Icon className="w-3 h-3" /> {label}
            </button>
          ))}
        </div>

        <div className="p-7">
          {tab === "items" && <ItemsTab memo={memo} drafts={drafts} isLocked={isLocked} setDraft={setDraft} doReturn={doReturn} doSell={doSell} busy={busy} />}
          {tab === "payments" && <PaymentsTab memo={memo} isAdmin={isAdmin} form={paymentForm} setForm={setPaymentForm} onAdd={addPayment} onRemove={removePayment} busy={busy} />}
          {tab === "timeline" && <TimelineTab timeline={timeline} />}
        </div>
      </div>
    </div>
  );
}

function ItemsTab({ memo, drafts, isLocked, setDraft, doReturn, doSell, busy }) {
  return (
    <div className="space-y-5">
      {memo.items.map((it) => (
        <LineCard key={it.line_id} line={it} draft={drafts[it.line_id]} isLocked={isLocked} setDraft={setDraft} />
      ))}
      {!isLocked && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5 sticky bottom-0 bg-[#0F0F0F] py-3">
          <button
            onClick={doReturn}
            disabled={busy}
            data-testid="apply-return"
            className="btn-outline-gold border-[#7BA585]/40 text-[#7BA585] px-5 py-2.5 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Apply Returns
          </button>
          <button
            onClick={doSell}
            disabled={busy}
            data-testid="apply-sell"
            className="btn-gold px-5 py-2.5 inline-flex items-center gap-2 text-xs uppercase tracking-wider"
          >
            <Check className="w-3.5 h-3.5" /> Apply Sales
          </button>
        </div>
      )}
    </div>
  );
}

function LineCard({ line, draft, isLocked, setDraft }) {
  const statusColor =
    line.item_status === "Sold" || line.item_status === "Settled"
      ? "border-[#D4AF37]/40 text-[#D4AF37]"
      : line.item_status === "Returned"
      ? "border-[#8A8A8A]/40 text-[#8A8A8A]"
      : line.item_status === "Partial Sold"
      ? "border-[#D4AF37]/40 text-[#D4AF37]"
      : line.item_status === "Partial Returned"
      ? "border-[#C99356]/40 text-[#C99356]"
      : "border-[#7BA585]/40 text-[#7BA585]";

  return (
    <div className="border border-white/10 p-4 grid grid-cols-1 md:grid-cols-12 gap-4 items-start" data-testid={`line-${line.product_sku}`}>
      <div className="md:col-span-1">
        {line.product_photo ? (
          <img src={line.product_photo} alt="" className="w-12 h-12 object-cover border border-white/10" />
        ) : (
          <div className="w-12 h-12 bg-[#141414] border border-white/5" />
        )}
      </div>
      <div className="md:col-span-4">
        <div className="text-sm">{line.product_name}</div>
        <div className="text-[0.7rem] mono text-[#666] mt-1">{line.product_sku} · {line.product_category || ""}</div>
        <div className="text-[0.7rem] mono text-[#8A8A8A] mt-1">
          Qty {line.quantity} · {formatCurrency(line.memo_price)} ea
        </div>
        <div className="mt-2 inline-block">
          <span className={`px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.16em] border ${statusColor}`} data-testid={`line-status-${line.product_sku}`}>
            {line.item_status}
          </span>
        </div>
      </div>
      <div className="md:col-span-2 text-xs text-[#8A8A8A]">
        <div><span className="text-[#666]">Returned</span> <span className="mono text-[#EAEAEA] ml-1">{line.qty_returned}</span></div>
        <div className="mt-1"><span className="text-[#666]">Sold</span> <span className="mono text-[#EAEAEA] ml-1">{line.qty_sold}</span></div>
        <div className="mt-1"><span className="text-[#666]">Left</span> <span className="mono text-[#D4AF37] ml-1" data-testid={`line-remaining-${line.product_sku}`}>{line.remaining_qty}</span></div>
      </div>
      {!isLocked && line.remaining_qty > 0 && (
        <div className="md:col-span-5 grid grid-cols-2 gap-2">
          <div>
            <div className="text-[0.6rem] mono uppercase tracking-wider text-[#7BA585] mb-1">Return qty</div>
            <input
              type="number" min="0" max={line.remaining_qty} step="1"
              value={draft?.return || ""}
              onChange={(e) => setDraft(line.line_id, "return", e.target.value)}
              className="luxe-input w-full px-2 py-1.5 text-xs mono"
              data-testid={`return-input-${line.product_sku}`}
              placeholder="0"
            />
          </div>
          <div>
            <div className="text-[0.6rem] mono uppercase tracking-wider text-[#D4AF37] mb-1">Sell qty</div>
            <input
              type="number" min="0" max={line.remaining_qty} step="1"
              value={draft?.sell || ""}
              onChange={(e) => setDraft(line.line_id, "sell", e.target.value)}
              className="luxe-input w-full px-2 py-1.5 text-xs mono"
              data-testid={`sell-input-${line.product_sku}`}
              placeholder="0"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ memo, isAdmin, form, setForm, onAdd, onRemove, busy }) {
  return (
    <div className="space-y-5" data-testid="payments-tab">
      {memo.converted_sale_id ? (
        <div className="border border-[#D4AF37]/30 bg-[#D4AF37]/[0.05] px-4 py-3 text-xs text-[#D4AF37]">
          This memo has converted to invoice <span className="mono">{memo.converted_invoice_number}</span>. Manage further payments on the Accounting page.
        </div>
      ) : (
        <form onSubmit={onAdd} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end border border-white/10 p-4">
          <div className="sm:col-span-5">
            <div className="overline mb-1">Amount (Rp)</div>
            <input
              type="number" min="1" step="1" required
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="luxe-input w-full px-3 py-2 text-sm mono"
              data-testid="payment-amount"
            />
          </div>
          <div className="sm:col-span-3">
            <div className="overline mb-1">Method</div>
            <select
              value={form.method}
              onChange={(e) => setForm({ ...form, method: e.target.value })}
              className="luxe-input w-full px-3 py-2 text-sm"
              data-testid="payment-method"
            >
              <option value="cash">Cash</option>
              <option value="wire">Wire</option>
              <option value="card">Card</option>
              <option value="qris">QRIS</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="sm:col-span-4">
            <button
              type="submit"
              disabled={busy}
              data-testid="add-payment-btn"
              className="btn-gold w-full px-5 py-2.5 inline-flex items-center justify-center gap-2 text-xs uppercase tracking-wider"
            >
              <Plus className="w-3.5 h-3.5" /> Add Payment
            </button>
          </div>
        </form>
      )}

      {(memo.payments || []).length === 0 ? (
        <div className="text-center text-xs text-[#666] py-8 border border-dashed border-white/10">No payments recorded yet.</div>
      ) : (
        <div className="space-y-2">
          {memo.payments.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-3 items-center border border-white/5 px-4 py-3 bg-white/[0.02]" data-testid={`payment-row-${p.id}`}>
              <div className="col-span-3 mono text-sm text-[#D4AF37]">{formatCurrency(p.amount)}</div>
              <div className="col-span-2 text-xs uppercase tracking-wider text-[#8A8A8A]">{p.method}</div>
              <div className="col-span-3 text-xs text-[#666] mono">{(p.date || p.recorded_at || "").slice(0, 10)}</div>
              <div className="col-span-3 text-[0.7rem] text-[#666]">{p.recorded_by_name}</div>
              {isAdmin && !memo.converted_sale_id && (
                <button
                  onClick={() => onRemove(p)}
                  data-testid={`delete-payment-${p.id}`}
                  className="col-span-1 text-[#8A8A8A] hover:text-red-400 inline-flex justify-end"
                  title="Remove (admin)"
                ><Trash2 className="w-3.5 h-3.5" strokeWidth={1.25} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimelineTab({ timeline }) {
  if (!timeline.length) {
    return <div className="text-xs text-[#666] py-8 text-center" data-testid="timeline-tab">Loading…</div>;
  }
  return (
    <div className="space-y-3" data-testid="timeline-tab">
      {timeline.map((e) => (
        <div key={e.id} className="grid grid-cols-12 gap-3 items-start border-l-2 border-[#D4AF37]/30 pl-3 py-1" data-testid={`tl-${e.kind}`}>
          <div className="col-span-3 text-[0.65rem] mono text-[#666]">{(e.ts || "").slice(0, 16).replace("T", " ")}</div>
          <div className="col-span-2 text-[0.65rem] uppercase tracking-wider text-[#D4AF37]">{e.kind}</div>
          <div className="col-span-7 text-xs text-[#EAEAEA]">
            {e.summary}
            <div className="text-[0.65rem] text-[#666] mt-0.5">by {e.actor_name || "system"}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Totals({ label, value, tone, testid }) {
  const toneClass =
    tone === "gold" ? "text-[#D4AF37]"
    : tone === "warn" ? "text-[#C99356]"
    : tone === "muted" ? "text-[#8A8A8A]"
    : "text-[#EAEAEA]";
  return (
    <div data-testid={testid}>
      <div className="overline mb-1">{label}</div>
      <div className={`mono text-sm ${toneClass}`}>{value}</div>
    </div>
  );
}
